import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { PageHeader, Empty } from "../components/ui-helpers";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Plus, ArrowLeft, WarningCircle, CheckCircle, Ticket, UploadSimple, PaperPlaneTilt, Trash, Gear } from "@phosphor-icons/react";
import { TrackBadge } from "./Sdp";
import SdpDevstar from "./SdpDevstar";

const STAGES = [
  { key: "assigned", label: "Assigned" },
  { key: "drafting", label: "Drafting" },
  { key: "in_review", label: "In Review" },
  { key: "approved", label: "Approved" },
  { key: "submitted", label: "Submitted" },
];
const CHILD_STATUS_CLS = { open: "bg-slate-100 text-slate-700", in_progress: "bg-blue-100 text-blue-800", completed: "bg-emerald-100 text-emerald-800", closed: "bg-emerald-100 text-emerald-800" };

export default function SdpProject() {
  const { id } = useParams();
  const nav = useNavigate();
  const [proj, setProj] = useState(null);
  const [users, setUsers] = useState([]);
  const [comments, setComments] = useState(null);
  const [approvals, setApprovals] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [assign, setAssign] = useState({ assigned_sme_id: "", requirement_reference: "", instructions: "" });
  const [pasteRows, setPasteRows] = useState([]);
  const [edit, setEdit] = useState({});

  const load = async () => {
    try {
      const [p, u, c, a] = await Promise.all([
        api.get(`/sdp/projects/${id}`), api.get("/users"),
        api.get(`/sdp/projects/${id}/comments`), api.get(`/sdp/approvals?project_id=${id}`)]);
      setProj(p.data); setUsers(u.data || []); setComments(c.data); setApprovals(a.data.approvals || []);
      setEdit({ current_phase: p.data.current_phase || "", comment_deadline: (p.data.comment_deadline || "").slice(0, 10), ballot_pool_status: p.data.ballot_pool_status || "not_joined", compliance_lead_id: p.data.compliance_lead_id || "", executive_approver_id: p.data.executive_approver_id || "" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to load project."); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const activate = async () => {
    try { await api.post(`/sdp/projects/${id}/activate`); toast.success("Parent milestone ticket created in ClaroFlow."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveSettings = async () => {
    try { await api.put(`/sdp/projects/${id}`, { name: proj.name, standard_ref: proj.standard_ref, process_track: proj.process_track, ...edit }); toast.success("Saved."); setSettingsOpen(false); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doAssign = async () => {
    if (!assign.assigned_sme_id || !assign.requirement_reference.trim()) { toast.error("Pick an SME and a requirement."); return; }
    try { await api.post(`/sdp/projects/${id}/drafts`, assign); toast.success("SME task assigned — child ticket created."); setAssignOpen(false); setAssign({ assigned_sme_id: "", requirement_reference: "", instructions: "" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removeDraft = async (did) => { try { await api.delete(`/sdp/drafts/${did}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const submitDraft = async (did) => { try { await api.post(`/sdp/drafts/${did}/submit`); toast.success("Marked submitted to NERC SBS."); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const routeApproval = async () => { try { const { data } = await api.post(`/sdp/projects/${id}/route-approval`); toast.success(`Routed ${data.routed} draft(s) for executive sign-off.`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const decide = async (aid, decision, notes) => { try { await api.post(`/sdp/approvals/${aid}/decision`, { decision, feedback_notes: notes || "" }); toast.success(decision === "approved" ? "Approved." : "Returned to SME."); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  // ---- Smart-Paste parsing (client-side, no upload) ----
  const mapHeader = (h) => {
    const k = (h || "").toLowerCase();
    if (/entity|company|organi|member|name/.test(k)) return "entity_name";
    if (/segment|seg/.test(k)) return "segment";
    if (/vote|position|affirm|negative|ballot/.test(k)) return "vote_position";
    if (/comment|feedback|reason|justif|text/.test(k)) return "comment_text";
    return null;
  };
  const parsePaste = (e) => {
    e.preventDefault();
    const html = e.clipboardData.getData("text/html");
    let matrix = [];
    if (html && /<table/i.test(html)) {
      const doc = new DOMParser().parseFromString(html, "text/html");
      matrix = Array.from(doc.querySelectorAll("tr")).map((tr) => Array.from(tr.querySelectorAll("th,td")).map((td) => td.innerText.trim()));
    } else {
      const text = e.clipboardData.getData("text/plain");
      matrix = text.split(/\r?\n/).filter((l) => l.trim()).map((l) => l.split("\t"));
    }
    if (matrix.length < 2) { toast.error("Paste a table with a header row and at least one data row."); return; }
    const map = matrix[0].map(mapHeader);
    if (!map.includes("entity_name") && !map.includes("vote_position")) { toast.error("Couldn't detect entity/vote columns from the header row."); return; }
    const rows = matrix.slice(1).map((r) => { const o = {}; map.forEach((f, i) => { if (f) o[f] = r[i] || ""; }); return o; }).filter((o) => o.entity_name || o.vote_position || o.comment_text);
    setPasteRows(rows);
    toast.success(`Parsed ${rows.length} rows — review and import.`);
  };
  const importPaste = async () => {
    try { const { data } = await api.post(`/sdp/projects/${id}/comments/import`, { rows: pasteRows }); toast.success(`Imported ${data.imported} comments.`); setPasteRows([]); setImportOpen(false); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const uploadFile = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post(`/sdp/projects/${id}/comments/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(`Imported ${data.imported} comments.`); setImportOpen(false); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const draftsByStage = useMemo(() => {
    const m = {}; STAGES.forEach((s) => (m[s.key] = []));
    (proj?.drafts || []).forEach((d) => { (m[d.status] = m[d.status] || []).push(d); });
    return m;
  }, [proj]);
  const childById = useMemo(() => Object.fromEntries((proj?.child_tickets || []).map((c) => [c.id, c])), [proj]);

  if (!proj) return <p className="text-sm text-muted-foreground p-6">Loading…</p>;
  const elig = proj.eligibility || {};

  return (
    <div data-testid="sdp-project-detail">
      <button onClick={() => nav("/sdp")} className="flex items-center gap-1 text-sm text-muted-foreground mb-3 hover:text-foreground"><ArrowLeft size={14} />All projects</button>
      <PageHeader title={<span className="flex items-center gap-3">{proj.name} <TrackBadge track={proj.process_track} /></span>}
        subtitle={`${proj.standard_ref || "—"} · Phase: ${proj.current_phase || "—"} · Deadline ${(proj.comment_deadline || "TBD").slice(0, 10)}`}
        action={<div className="flex gap-2">
          <Button variant="outline" className="rounded-none" data-testid="sdp-settings-btn" onClick={() => setSettingsOpen(true)}><Gear size={15} className="mr-1" />Settings</Button>
          {!proj.parent_ticket_id && <Button className="rounded-none" data-testid="sdp-activate-btn" onClick={activate}><Ticket size={15} className="mr-1" />Activate phase</Button>}
        </div>} />

      {elig.warning ?
        <div data-testid="sdp-eligibility-banner" className="flex items-center gap-2 mb-4 text-sm text-red-800 bg-red-50 border border-red-300 px-3 py-2">
          <WarningCircle size={18} weight="fill" /><b>{elig.warning}.</b> {elig.detail}
        </div> :
        <div data-testid="sdp-eligibility-banner" className="flex items-center gap-2 mb-4 text-sm text-emerald-800 bg-emerald-50 border border-emerald-300 px-3 py-2">
          <CheckCircle size={18} weight="fill" /><b>Voting eligible.</b> {elig.detail}
        </div>}

      <Tabs defaultValue="tasks">
        <TabsList className="rounded-none">
          <TabsTrigger value="tasks" data-testid="sdp-tab-tasks" className="rounded-none">SME Tasks</TabsTrigger>
          <TabsTrigger value="comments" data-testid="sdp-tab-comments" className="rounded-none">Industry Comments ({comments?.total || 0})</TabsTrigger>
          <TabsTrigger value="exec" data-testid="sdp-tab-exec" className="rounded-none">Executive Review ({approvals.length})</TabsTrigger>
          <TabsTrigger value="devstar" data-testid="sdp-tab-devstar" className="rounded-none">DevSTaR</TabsTrigger>
        </TabsList>

        {/* --------- SME Tasks Kanban --------- */}
        <TabsContent value="tasks">
          <div className="flex justify-between items-center my-3">
            <p className="text-sm text-muted-foreground">Assign requirements to SMEs — each creates a linked child ticket in ClaroFlow.</p>
            <Button size="sm" className="rounded-none" data-testid="sdp-assign-btn" onClick={() => setAssignOpen(true)}><Plus size={15} className="mr-1" />Assign SME</Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3" data-testid="sdp-kanban">
            {STAGES.map((s) => (
              <div key={s.key} data-testid={`sdp-col-${s.key}`} className="bg-secondary/40 border border-border p-2 min-h-[120px]">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 flex justify-between"><span>{s.label}</span><span>{draftsByStage[s.key]?.length || 0}</span></div>
                <div className="space-y-2">
                  {(draftsByStage[s.key] || []).map((d) => {
                    const child = childById[d.child_ticket_id] || {};
                    return (
                      <div key={d.id} data-testid={`sdp-card-${d.id}`} className="bg-card border border-border p-2 text-xs">
                        <div className="font-semibold">{d.requirement_reference}</div>
                        <div className="text-[11px] text-muted-foreground truncate">{d.assigned_sme_id}</div>
                        <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                          <span className={`text-[10px] px-1.5 py-0.5 ${CHILD_STATUS_CLS[child.status] || "bg-slate-100"}`}><Ticket size={9} className="inline mr-0.5" />{(child.status || "—").replace("_", " ")}</span>
                          {child.due_date && <span className="text-[10px] text-muted-foreground">due {child.due_date}</span>}
                        </div>
                        <div className="flex gap-1 mt-2">
                          <button data-testid={`sdp-open-draft-${d.id}`} onClick={() => nav(`/sdp/draft/${d.id}`)} className="text-[11px] text-[#071B3E] font-semibold underline">Open</button>
                          {d.status === "approved" && <button data-testid={`sdp-submit-${d.id}`} onClick={() => submitDraft(d.id)} className="text-[11px] text-emerald-700 font-semibold ml-auto">Mark submitted</button>}
                          <button onClick={() => removeDraft(d.id)} className="text-slate-400 hover:text-red-600 ml-auto"><Trash size={12} /></button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* --------- Industry Comments --------- */}
        <TabsContent value="comments">
          <div className="flex justify-between items-center my-3">
            <p className="text-sm text-muted-foreground">Ingested peer-utility & RTO feedback from the NERC SBS.</p>
            <Button size="sm" className="rounded-none" data-testid="sdp-import-btn" onClick={() => setImportOpen(true)}><UploadSimple size={15} className="mr-1" />Import Industry Comments</Button>
          </div>
          {comments && comments.total > 0 ? <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="border border-border p-3"><h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">Position breakdown</h4>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={[{ n: "Affirmative", v: comments.position_breakdown.affirmative, c: "#059669" }, { n: "Negative", v: comments.position_breakdown.negative, c: "#dc2626" }, { n: "Abstain", v: comments.position_breakdown.abstain, c: "#64748b" }, { n: "Other", v: comments.position_breakdown.other, c: "#94a3b8" }]}>
                    <XAxis dataKey="n" tick={{ fontSize: 11 }} /><YAxis allowDecimals={false} tick={{ fontSize: 11 }} /><Tooltip />
                    <Bar dataKey="v">{["#059669", "#dc2626", "#64748b", "#94a3b8"].map((c, i) => <Cell key={i} fill={c} />)}</Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="border border-border p-3"><h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">Segment breakdown</h4>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={comments.segment_breakdown.map((s) => ({ n: `Seg ${s.segment}`, v: s.count }))}>
                    <XAxis dataKey="n" tick={{ fontSize: 11 }} /><YAxis allowDecimals={false} tick={{ fontSize: 11 }} /><Tooltip /><Bar dataKey="v" fill="#071B3E" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="border border-border max-h-[420px] overflow-auto">
              <table className="w-full text-sm" data-testid="sdp-comment-table">
                <thead className="bg-secondary/60 sticky top-0"><tr className="text-left"><th className="p-2">Entity</th><th className="p-2">Seg</th><th className="p-2">Position</th><th className="p-2">Comment</th></tr></thead>
                <tbody>{comments.comments.map((c) => <tr key={c.id} className="border-t border-border"><td className="p-2 font-medium">{c.entity_name}</td><td className="p-2">{c.segment}</td><td className="p-2">{c.vote_position}</td><td className="p-2 text-xs text-muted-foreground">{c.comment_text}</td></tr>)}</tbody>
              </table>
            </div>
          </> : <Empty title="No industry comments yet" hint="Import from the NERC SBS via smart-paste or a CSV/XLSX file." />}
        </TabsContent>

        {/* --------- Executive Review --------- */}
        <TabsContent value="exec">
          <div className="flex justify-between items-center my-3">
            <p className="text-sm text-muted-foreground">Consolidated SME technical inputs awaiting executive sign-off.</p>
            <Button size="sm" className="rounded-none" data-testid="sdp-route-approval-btn" onClick={routeApproval}><PaperPlaneTilt size={15} className="mr-1" />Route in-review drafts for sign-off</Button>
          </div>
          {approvals.length === 0 ? <Empty title="Nothing pending sign-off" hint="Route drafts that SMEs have marked complete (in review) to the executive approver." /> :
            <div className="space-y-3">
              {approvals.map((a) => <ExecCard key={a.id} a={a} onDecide={decide} />)}
            </div>}
        </TabsContent>

        {/* --------- DevSTaR (AI standard-change evaluation) --------- */}
        <TabsContent value="devstar">
          <SdpDevstar projectId={id} />
        </TabsContent>
      </Tabs>

      {/* Assign SME dialog */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="rounded-none" data-testid="sdp-assign-dialog">
          <DialogHeader><DialogTitle>Assign SME task</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Requirement reference</Label><Input data-testid="sdp-assign-req" value={assign.requirement_reference} onChange={(e) => setAssign({ ...assign, requirement_reference: e.target.value })} placeholder="PRC-029-1 R1" className="rounded-none" /></div>
            <div><Label>Assign to SME</Label>
              <Select value={assign.assigned_sme_id} onValueChange={(v) => setAssign({ ...assign, assigned_sme_id: v })}>
                <SelectTrigger className="rounded-none" data-testid="sdp-assign-sme"><SelectValue placeholder="Select user" /></SelectTrigger>
                <SelectContent>{users.map((u) => <SelectItem key={u.email} value={u.email}>{u.name} ({u.email})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Instructions (optional)</Label><Textarea data-testid="sdp-assign-notes" value={assign.instructions} onChange={(e) => setAssign({ ...assign, instructions: e.target.value })} rows={2} className="rounded-none" /></div>
            <Button data-testid="sdp-assign-submit" onClick={doAssign} className="w-full rounded-none">Assign & create child ticket</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import comments dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="rounded-none max-w-2xl" data-testid="sdp-import-dialog">
          <DialogHeader><DialogTitle>Import Industry Comments</DialogTitle></DialogHeader>
          <Tabs defaultValue="paste">
            <TabsList className="rounded-none"><TabsTrigger value="paste" className="rounded-none">Smart Paste</TabsTrigger><TabsTrigger value="file" className="rounded-none">File Upload</TabsTrigger></TabsList>
            <TabsContent value="paste">
              <p className="text-xs text-muted-foreground my-2">Copy the results grid from the NERC SBS and paste below. We detect the entity / segment / vote / comment columns automatically — nothing leaves your browser until you import.</p>
              <textarea data-testid="sdp-paste-box" onPaste={parsePaste} placeholder="Paste table here…" className="w-full h-24 border border-dashed border-border p-2 text-sm rounded-none" />
              {pasteRows.length > 0 && <>
                <div className="text-xs text-emerald-700 my-2">{pasteRows.length} rows ready.</div>
                <div className="border border-border max-h-40 overflow-auto text-xs"><table className="w-full"><thead className="bg-secondary/60"><tr><th className="p-1 text-left">Entity</th><th className="p-1">Seg</th><th className="p-1">Vote</th><th className="p-1 text-left">Comment</th></tr></thead><tbody>{pasteRows.slice(0, 20).map((r, i) => <tr key={i} className="border-t border-border"><td className="p-1">{r.entity_name}</td><td className="p-1 text-center">{r.segment}</td><td className="p-1 text-center">{r.vote_position}</td><td className="p-1 truncate max-w-[240px]">{r.comment_text}</td></tr>)}</tbody></table></div>
                <Button data-testid="sdp-paste-import" onClick={importPaste} className="w-full rounded-none mt-2">Import {pasteRows.length} comments</Button>
              </>}
            </TabsContent>
            <TabsContent value="file">
              <label data-testid="sdp-file-drop" className="mt-2 flex flex-col items-center justify-center border-2 border-dashed border-border p-8 cursor-pointer hover:bg-secondary/30"
                onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); uploadFile(e.dataTransfer.files?.[0]); }}>
                <UploadSimple size={28} className="text-muted-foreground" />
                <span className="text-sm mt-2">Drop a .csv or .xlsx, or click to browse</span>
                <input type="file" accept=".csv,.xlsx" className="hidden" onChange={(e) => uploadFile(e.target.files?.[0])} />
              </label>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Settings dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="rounded-none" data-testid="sdp-settings-dialog">
          <DialogHeader><DialogTitle>Project settings</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Current phase</Label><Input value={edit.current_phase} onChange={(e) => setEdit({ ...edit, current_phase: e.target.value })} className="rounded-none" placeholder="comment / balloting" /></div>
              <div><Label>Deadline</Label><Input type="date" value={edit.comment_deadline} onChange={(e) => setEdit({ ...edit, comment_deadline: e.target.value })} className="rounded-none" /></div>
            </div>
            {proj.process_track === "legacy" &&
              <div><Label>Ballot pool status</Label>
                <Select value={edit.ballot_pool_status} onValueChange={(v) => setEdit({ ...edit, ballot_pool_status: v })}>
                  <SelectTrigger className="rounded-none" data-testid="sdp-set-ballot"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="not_joined">Not joined</SelectItem><SelectItem value="joined">Joined pool</SelectItem></SelectContent>
                </Select>
              </div>}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Compliance Lead</Label>
                <Select value={edit.compliance_lead_id} onValueChange={(v) => setEdit({ ...edit, compliance_lead_id: v })}>
                  <SelectTrigger className="rounded-none"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{users.map((u) => <SelectItem key={u.email} value={u.email}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Executive approver</Label>
                <Select value={edit.executive_approver_id} onValueChange={(v) => setEdit({ ...edit, executive_approver_id: v })}>
                  <SelectTrigger className="rounded-none"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{users.map((u) => <SelectItem key={u.email} value={u.email}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <Button data-testid="sdp-settings-save" onClick={saveSettings} className="w-full rounded-none">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ExecCard({ a, onDecide }) {
  const [notes, setNotes] = useState("");
  const [rejecting, setRejecting] = useState(false);
  return (
    <div data-testid={`sdp-approval-${a.id}`} className="border border-border bg-card p-3">
      <div className="flex items-center justify-between">
        <div className="font-semibold text-sm">{a.requirement_reference}</div>
        <span className="text-xs text-muted-foreground">SME: {a.draft?.assigned_sme_id}</span>
      </div>
      <div className="mt-2 text-sm bg-secondary/40 border border-border p-2 whitespace-pre-wrap max-h-52 overflow-auto">{a.draft?.draft_text || "(no text)"}</div>
      {rejecting && <Textarea data-testid={`sdp-reject-notes-${a.id}`} value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Notes for the SME…" className="rounded-none mt-2" />}
      <div className="flex gap-2 mt-2">
        <Button size="sm" className="rounded-none bg-emerald-700 hover:bg-emerald-800" data-testid={`sdp-approve-${a.id}`} onClick={() => onDecide(a.id, "approved")}>Approve</Button>
        {rejecting ?
          <Button size="sm" variant="outline" className="rounded-none border-red-400 text-red-700" data-testid={`sdp-reject-confirm-${a.id}`} onClick={() => onDecide(a.id, "rejected", notes)}>Confirm reject</Button> :
          <Button size="sm" variant="outline" className="rounded-none" data-testid={`sdp-reject-${a.id}`} onClick={() => setRejecting(true)}>Reject with notes</Button>}
      </div>
    </div>
  );
}
